import { Bell, Inbox, AlertTriangle, Archive as ArchiveIcon, CalendarIcon } from 'lucide-react';
import { type ReactElement, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, IntlProvider, useIntl } from 'react-intl';

import { Calendar } from '../ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useHistory } from 'react-router-dom';
import {
  CourierInbox,
  archiveMessage,
  markAsRead,
  markAsUnread,
  type CourierInboxFeed,
  type CourierInboxListItemFactoryProps,
} from '@trycourier/courier-react';

import { Button } from '../ui/button';

import { courierInboxTheme } from './courierInboxTheme';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

import NotificationCard, { type NotificationKind } from './NotificationCard';
import { useCourierAuth } from './useCourierAuth';

// Date-range presets fed into the SDK's `from` filter (lower bound on
// created timestamp). 'all' = no `from`, fetch the full history.
// 'custom' lets the user pick an arbitrary "since" date via a date input.
type DateRange = 'all' | 'today' | '7d' | '30d' | 'custom';
const DATE_LABELS: Record<DateRange, string> = {
  all: 'All time',
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  custom: 'Custom…',
};
type CustomRange = { from?: string; to?: string }; // ISO yyyy-mm-dd

const dateRangeToFrom = (r: DateRange, custom: CustomRange): string | undefined => {
  if (r === 'all') return undefined;
  if (r === 'today') {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  if (r === 'custom') {
    if (!custom.from) return undefined;
    const d = new Date(`${custom.from}T00:00:00`);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
  }
  const days = r === '7d' ? 7 : 30;
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
};

// Build feeds (Notifications / Archived) with the current date filter applied.
// Server-side `from` is a lower bound; the custom `to` is applied
// client-side inside renderListItem (Courier SDK has no upper bound).
const buildFeeds = (
  view: 'inbox' | 'archived',
  range: DateRange,
  custom: CustomRange,
  notificationsTitle: string,
  archivedTitle: string
): CourierInboxFeed[] => {
  const from = dateRangeToFrom(range, custom);
  const baseFilter = from ? { from } : {};
  const key = range === 'custom' ? `custom-${custom.from || 'na'}-${custom.to || 'na'}` : range;
  if (view === 'archived') {
    return [{
      feedId: 'archived',
      title: archivedTitle,
      tabs: [{ datasetId: `inbox-archived-${key}`, title: '', filter: { ...baseFilter, archived: true } }],
    }];
  }
  return [{
    feedId: 'inbox',
    title: notificationsTitle,
    tabs: [{ datasetId: `inbox-active-${key}`, title: '', filter: baseFilter }],
  }];
};

const getKind = (message: CourierInboxListItemFactoryProps['message']): NotificationKind => {
  if (message.tags?.includes('marketing')) return 'marketing';
  if (message.tags?.includes('system')) return 'system';
  return 'transaction';
};

const getHref = (message: CourierInboxListItemFactoryProps['message']) => {
  const hrefFromAction = message.actions?.find(action => action.href)?.href;
  const hrefFromData = typeof message.data?.href === 'string' ? message.data.href : undefined;
  const threadId = typeof message.data?.threadId === 'string' ? message.data.threadId : undefined;
  return hrefFromAction ?? hrefFromData ?? (threadId ? `/inbox#thread=${threadId}` : '/inbox');
};

const getImage = (message: CourierInboxListItemFactoryProps['message']) => {
  const listingImage = message.data?.listingImage;
  return typeof listingImage === 'string' ? listingImage : undefined;
};

type NotificationBellProps = {
  triggerClassName?: string;
  iconClassName?: string;
  dotClassName?: string;
};

const NotificationBell = ({
  triggerClassName,
  iconClassName,
  dotClassName,
}: NotificationBellProps = {}) => {
  const history = useHistory();
  const intl = useIntl();
  const { isReady, totalUnreadCount } = useCourierAuth();
  const [view, setView] = useState<'inbox' | 'archived'>('inbox');
  const [range, setRange] = useState<DateRange>('all');
  const [custom, setCustom] = useState<CustomRange>({});
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarWrapRef = useRef<HTMLDivElement | null>(null);

  const notificationsTitle = intl.formatMessage({ id: 'NotificationBell.feed.notifications' });
  const archivedTitle = intl.formatMessage({ id: 'NotificationBell.feed.archived' });
  const titleFallback = intl.formatMessage({ id: 'NotificationBell.item.titleFallback' });
  const previewFallback = intl.formatMessage({ id: 'NotificationBell.item.previewFallback' });
  const justNowLabel = intl.formatMessage({ id: 'NotificationBell.item.metaJustNow' });

  const feeds = useMemo(
    () => buildFeeds(view, range, custom, notificationsTitle, archivedTitle),
    [view, range, custom, notificationsTitle, archivedTitle]
  );
  const customToCutoff = useMemo(() => {
    if (range !== 'custom' || !custom.to) return null;
    const d = new Date(`${custom.to}T23:59:59.999`);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }, [range, custom]);

  // Click-outside close for the inline calendar popover.
  useEffect(() => {
    if (!calendarOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (calendarWrapRef.current && !calendarWrapRef.current.contains(e.target as Node)) {
        setCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [calendarOpen]);

  const fmtShort = (iso?: string) => {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };
  const customLabel = (() => {
    const f = fmtShort(custom.from);
    const t = fmtShort(custom.to);
    if (f && t) return `${f} – ${t}`;
    if (f) return `From ${f}`;
    return 'Pick range';
  })();

  // Courier renders every slot (list item, empty/error/loading states) in its
  // own React root via createRoot, detached from the app's <IntlProvider>. Any
  // FormattedMessage/useIntl inside a slot needs intl re-seeded here.
  const withCourierIntl = (node: ReactNode): ReactElement => (
    <IntlProvider
      locale={intl.locale}
      defaultLocale={intl.defaultLocale}
      messages={intl.messages}
    >
      {node}
    </IntlProvider>
  );

  const renderListItem = (props: CourierInboxListItemFactoryProps | undefined | null) => {
    if (!props?.message) return null;
    const m = props.message;
    const dataTitle = typeof m.data?.title === 'string' ? m.data.title : undefined;
    const dataPreview = typeof m.data?.preview === 'string' ? m.data.preview : undefined;
    const title = m.title ?? dataTitle ?? titleFallback;
    const preview = m.preview ?? dataPreview ?? m.body ?? previewFallback;

    // Client-side upper-bound for the custom range (Courier `from` is
    // lower-bound only; no native `to` filter).
    if (customToCutoff && m.created) {
      const created = new Date(m.created).getTime();
      if (!Number.isNaN(created) && created > customToCutoff) return null;
    }

    return withCourierIntl(
      <NotificationCard
        title={title}
        preview={preview}
        meta={
          m.created
            ? new Date(m.created).toLocaleString([], {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })
            : justNowLabel
        }
        href={getHref(m)}
        unread={!m.read}
        kind={getKind(m)}
        imageSrc={getImage(m)}
        onOpen={href => history.push(href)}
        onArchive={() => { archiveMessage(m); }}
        onToggleRead={() => { m.read ? markAsUnread(m) : markAsRead(m); }}
        dataTestId={`courier-message-${m.messageId}`}
      />
    );
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={
            triggerClassName ??
            'relative inline-flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:text-primary transition-colors'
          }
          data-testid="button-notification-bell"
          aria-label={intl.formatMessage({ id: 'NotificationBell.ariaLabel' })}
        >
          <Bell className={iconClassName ?? 'h-5 w-5'} strokeWidth={2.25} />
          {totalUnreadCount > 0 ? (
            <span
              className={
                dotClassName ?? 'absolute top-2 right-2 h-2 w-2 bg-primary rounded-full'
              }
            />
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[420px] max-w-[calc(100vw-1rem)] p-0 overflow-hidden"
        data-testid="panel-notification-center"
      >
        {!isReady ? (
          <div className="flex h-[120px] items-center justify-center text-xs text-muted-foreground">
            <FormattedMessage id="NotificationBell.loading" />
          </div>
        ) : (
        <>
          <div className="flex items-center justify-end gap-1.5 border-b border-[#e4ddd4] bg-[#fcfaf7] px-3 py-2">
            <Select
              value={range}
              onValueChange={(v) => {
                const next = v as DateRange;
                setRange(next);
                if (next !== 'custom') setCustom({});
                setCalendarOpen(next === 'custom' && !custom.from);
              }}
            >
              <SelectTrigger
                className="h-7 w-auto gap-1 rounded-full border-[#e4ddd4] bg-white px-2.5 text-[11px] font-medium text-[#7a7267] focus:ring-0"
                data-testid="select-notification-date-range"
                aria-label="Filter by date"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end" className="min-w-[140px]">
                {(Object.keys(DATE_LABELS) as DateRange[]).map((r) => (
                  <SelectItem key={r} value={r} className="text-xs">{DATE_LABELS[r]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {range === 'custom' && (
              <div className="relative" ref={calendarWrapRef}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setCalendarOpen((o) => !o); }}
                  className="flex h-7 items-center gap-1 rounded-full border border-[#e4ddd4] bg-white px-2.5 text-[11px] font-medium text-[#7a7267] hover:border-primary/30 hover:text-primary"
                  data-testid="button-notification-custom-date"
                  aria-haspopup="dialog"
                  aria-expanded={calendarOpen}
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {customLabel}
                </button>
                {calendarOpen && (
                  <div
                    role="dialog"
                    onClick={(e) => e.stopPropagation()}
                    style={{ zIndex: 9999 }}
                    className="absolute right-0 top-9 rounded-md border border-[#e4ddd4] bg-white p-1 shadow-lg isolate"
                  >
                    <Calendar
                      mode="range"
                      selected={
                        custom.from
                          ? {
                              from: new Date(`${custom.from}T00:00:00`),
                              to: custom.to ? new Date(`${custom.to}T00:00:00`) : undefined,
                            }
                          : undefined
                      }
                      onSelect={(r) => {
                        if (!r) { setCustom({}); return; }
                        const toIso = (d?: Date) =>
                          d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined;
                        const fromIso = toIso(r.from);
                        const toIsoStr = toIso(r.to);
                        // react-day-picker sets to===from on the first click of a range.
                        // Treat single-day ranges as "from only" so the second click can extend.
                        setCustom({
                          from: fromIso,
                          to: toIsoStr && toIsoStr !== fromIso ? toIsoStr : undefined,
                        });
                      }}
                      disabled={(d) => d > new Date()}
                      numberOfMonths={1}
                      className="bg-white"
                    />
                    <div className="flex items-center justify-between gap-2 border-t border-[#e4ddd4] px-2 py-1.5">
                      <button
                        type="button"
                        onClick={() => { setCustom({}); }}
                        className="text-[11px] font-medium text-[#7a7267] hover:text-primary"
                        data-testid="button-notification-custom-clear"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={() => setCalendarOpen(false)}
                        className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-medium text-primary hover:bg-primary/15"
                        data-testid="button-notification-custom-done"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={() => setView(view === 'archived' ? 'inbox' : 'archived')}
              className={
                'flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ' +
                (view === 'archived'
                  ? 'border-primary/30 bg-primary/10 text-primary'
                  : 'border-[#e4ddd4] bg-white text-[#7a7267] hover:border-primary/30 hover:text-primary')
              }
              data-testid="button-toggle-archived"
              aria-pressed={view === 'archived'}
            >
              {view === 'archived' ? <Bell className="h-3.5 w-3.5" /> : <ArchiveIcon className="h-3.5 w-3.5" />}
              <FormattedMessage
                id={view === 'archived' ? 'NotificationBell.toggleToNotifications' : 'NotificationBell.toggleToArchived'}
              />
            </button>
          </div>
        <CourierInbox
          feeds={feeds}
          lightTheme={courierInboxTheme}
          height="560px"
          renderListItem={renderListItem}
          renderEmptyState={() => withCourierIntl(
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-10 text-center">
              <Inbox className="h-8 w-8 text-muted-foreground" />
              <h3 className="font-heading text-sm font-bold uppercase tracking-wide">
                <FormattedMessage id="NotificationBell.empty.title" />
              </h3>
              <p className="text-xs text-muted-foreground">
                <FormattedMessage id="NotificationBell.empty.subtitle" />
              </p>
            </div>
          )}
          renderErrorState={({ error }) => withCourierIntl(
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-10 text-center">
              <AlertTriangle className="h-8 w-8 text-destructive" />
              <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-destructive">
                <FormattedMessage id="NotificationBell.error.title" />
              </h3>
              <p className="text-xs text-muted-foreground">
                {error?.message ?? intl.formatMessage({ id: 'NotificationBell.error.subtitle' })}
              </p>
              <Button size="sm" onClick={() => window.location.reload()}>
                <FormattedMessage id="NotificationBell.error.retry" />
              </Button>
            </div>
          )}
          renderLoadingState={() => withCourierIntl(
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-10 text-center">
              <div className="h-8 w-8 animate-pulse rounded-full bg-muted" />
              <p className="text-xs text-muted-foreground">
                <FormattedMessage id="NotificationBell.loading" />
              </p>
            </div>
          )}
          onMessageClick={props => history.push(getHref(props.message))}
        />
        </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
